import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, CheckCircle2, XCircle, RefreshCcw, Loader2 } from "lucide-react";

const FILTERS = ["all", "paid", "shipped", "fulfilled", "cancelled"];
const STATUS_COLOR = { paid: "#FFD60A", shipped: "#5AC8FA", fulfilled: "#32D74B", cancelled: "#FF3B30" };
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const SM = "btn-ghost text-xs inline-flex items-center gap-1.5 !px-3 !py-2";
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

function Address({ s }) {
  if (!s) return <div className="text-xs text-[var(--ff-text-dim)] italic">No address on file</div>;
  return (
    <div className="text-xs font-mono-ui leading-relaxed">
      <div className="text-white">{s.name}</div>
      <div>{s.line1}{s.line2 ? `, ${s.line2}` : ""}</div>
      <div>{[s.city, s.state, s.postal_code].filter(Boolean).join(", ")} {s.country}</div>
    </div>
  );
}

function OrderActions({ o, onUpdate }) {
  const [shipping, setShipping] = useState(false);
  const [tracking, setTracking] = useState(o.tracking || "");
  const [busy, setBusy] = useState(false);
  const go = async (status, extra = {}) => {
    setBusy(true);
    await onUpdate(o.id, { status, ...extra });
    setBusy(false);
    setShipping(false);
  };
  if (shipping) {
    return (
      <div className="flex gap-2 flex-wrap" data-testid={`order-ship-form-${o.id}`}>
        <input autoFocus placeholder="Tracking number (optional)" value={tracking} onChange={(e) => setTracking(e.target.value)} className="flex-1 min-w-[180px] px-3 py-2 text-xs" data-testid={`order-tracking-input-${o.id}`} />
        <button disabled={busy} onClick={() => go("shipped", { tracking })} className="btn-primary text-xs inline-flex items-center gap-1.5 !px-3 !py-2" data-testid={`order-confirm-ship-${o.id}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Truck size={12} />} Confirm Shipped
        </button>
        <button onClick={() => setShipping(false)} className={SM} data-testid={`order-cancel-ship-${o.id}`}>Cancel</button>
      </div>
    );
  }
  return (
    <div className="flex gap-2 flex-wrap">
      {o.requires_shipping && o.status !== "cancelled" && (
        <button onClick={() => setShipping(true)} className={SM} data-testid={`order-ship-btn-${o.id}`}>
          <Truck size={12} /> {o.status === "shipped" ? "Edit Tracking" : "Mark Shipped"}
        </button>
      )}
      {o.status !== "fulfilled" && o.status !== "cancelled" && (
        <button disabled={busy} onClick={() => go("fulfilled")} className={SM} data-testid={`order-fulfill-btn-${o.id}`}><CheckCircle2 size={12} /> Mark Fulfilled</button>
      )}
      {o.status !== "cancelled" ? (
        <button disabled={busy} onClick={() => window.confirm("Mark this order cancelled? (Refunds must be issued in Stripe/PayPal.)") && go("cancelled")} className={`${SM} text-[var(--ff-red)]`} data-testid={`order-cancel-btn-${o.id}`}><XCircle size={12} /> Cancel</button>
      ) : (
        <button disabled={busy} onClick={() => go("paid")} className={SM} data-testid={`order-reopen-btn-${o.id}`}>Reopen</button>
      )}
    </div>
  );
}

function OrderCard({ o, onUpdate }) {
  return (
    <div className="surface-card p-5 grid md:grid-cols-12 gap-4" data-testid={`merch-order-${o.id}`}>
      <div className="md:col-span-4 space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] px-2 py-0.5 font-bold text-black" style={{ background: STATUS_COLOR[o.status] || "#999" }} data-testid={`order-status-${o.id}`}>{o.status}</span>
          <span className="label-eyebrow">{o.provider}</span>
        </div>
        <div className="font-display text-lg leading-tight">{o.item_name}{o.variant ? ` · ${o.variant}` : ""} × {o.quantity}</div>
        <div className="text-xs text-[var(--ff-text-dim)] font-mono-ui">{o.show_name} · {fmtDate(o.paid_at)}</div>
        <div className="font-display text-xl text-[var(--ff-red)]">{money(o.amount_cents)}</div>
      </div>
      <div className="md:col-span-3 text-xs font-mono-ui space-y-1">
        <div className="label-eyebrow">Buyer</div>
        <div className="text-white">{o.buyer_name || "—"}</div>
        <div className="text-[var(--ff-text-dim)] break-all">{o.buyer_email || "—"}</div>
        {o.buyer_discord_id && <div className="text-[var(--ff-text-dim)]">Discord: {o.buyer_discord_id}</div>}
      </div>
      <div className="md:col-span-5 space-y-3">
        {o.requires_shipping ? <><div className="label-eyebrow">Ship To</div><Address s={o.shipping} /></> : <div className="label-eyebrow">Digital item — no shipping</div>}
        {o.tracking && <div className="text-xs font-mono-ui" data-testid={`order-tracking-${o.id}`}>Tracking: <span className="text-white">{o.tracking}</span></div>}
        <OrderActions o={o} onUpdate={onUpdate} />
      </div>
    </div>
  );
}

export default function MerchOrdersAdmin() {
  const [filter, setFilter] = useState("all");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/merch-orders", { params: filter === "all" ? {} : { status: filter } });
      setOrders(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (id, body) => {
    try {
      const { data } = await api.put(`/admin/merch-orders/${id}`, body);
      toast.success(`Order marked ${data.status}`);
      setOrders((list) => (filter === "all" || data.status === filter ? list.map((o) => (o.id === id ? data : o)) : list.filter((o) => o.id !== id)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  return (
    <div data-testid="merch-orders-admin">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`orders-filter-${f}`}
            className={`px-4 py-2 font-mono-ui text-xs uppercase tracking-[0.15em] border transition-colors ${filter === f ? "bg-[var(--ff-red)] border-[var(--ff-red)] text-white" : "border-[var(--ff-border)] text-[var(--ff-text-dim)] hover:text-white"}`}>
            {f}
          </button>
        ))}
        <button onClick={load} className={`${SM} ml-auto`} data-testid="orders-refresh-btn"><RefreshCcw size={12} /> Refresh</button>
      </div>
      {loading ? <div className="text-[var(--ff-text-dim)] text-sm">Loading orders…</div>
        : orders.length === 0 ? <div className="surface-card p-6 text-[var(--ff-text-dim)] italic" data-testid="orders-empty">No orders here yet.</div>
        : <div className="space-y-3">{orders.map((o) => <OrderCard key={o.id} o={o} onUpdate={update} />)}</div>}
    </div>
  );
}
